import { ComponentBase } from 'utils/abstract-classes/component-base'
import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import {
    AriaAttr,
    EventType,
    HtmlAttr,
    HTMLElementType,
    InputType
} from 'constants/enums'

interface validationType {
    names?: string[]
    regex: RegExp
    error_text: string
    type?: string
}

export class FormValidatorComponent extends ComponentBase {
    private inputContainers: HTMLElement[]
    private submitButtons: HTMLElement[]
    private REQUIRED_ERROR_TEXT = 'This is a required field.'
    private CHECKBOX_GROUP_ERROR_TEXT = 'Please select at least 1 option.'
    private RECAPTCHA = 'g-recaptcha-response'

    private defaultValidation: validationType = {
        regex: /^([()a-zA-Z0-9àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ'.\-\s,/:#]*)$/g,
        error_text:
            'Please use English, French, or ()-,./:# characters and numbers only.'
    }
    private validationTypes: validationType[] = [
        {
            names: ['phone_number', 'phone', 'school_phone_number'],
            regex: /^(([+\d\s]{3})?[\d]{3}?[-][\d]{3}[-][\d]{4})$/im,
            error_text: 'Valid phone number is required.',
            type: InputType.TEL
        },
        {
            names: ['postal_code'],
            regex: /^(\d{5}(-\d{4})?|^[abceghj-nprstvxyABCEGHJ-NPRSTVXY]\d[abceghj-nprstv-zABCEGHJ-NPRSTV-Z] ?\d[abceghj-nprstv-z-ABCEGHJ-NPRSTV-Z]\d)$/g,
            error_text: 'Must be a valid North American Postal Code'
        },
        {
            names: [
                'email_address',
                'email',
                'confirm_email_address',
                'confirm_email',
                'school_email_address',
                'teacher_email_address'
            ],
            regex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            error_text: 'Valid email address is required.',
            type: InputType.EMAIL
        },
        {
            names: [
                'first_name',
                'last_name',
                'full_name',
                'sponsors',
                'title',
                'contact_name',
                'organization_name',
                'subscriber_first_name',
                'subscriber_last_name',
                'school_name',
                'teacher_full_name'
            ],
            regex: /^([a-zA-ZàâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ'\-\s']*)$/g,
            error_text:
                'Please use English, French, hyphens or apostrophes characters only.'
        },
        {
            names: ['street_address', 'full_address', 'school_address'],
            regex: /^([()a-zA-Z0-9àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ'.\-\s,/:#]*)$/g,
            error_text:
                'Please use English, French, or ()-,./:# characters and numbers only.'
        },
        {
            names: ['url', 'website', 'facebook', 'linkedin', 'twitter'],
            regex: /^(http|HTTP[s|S]?:\/\/){0,1}(w{3,3}\.)?[-a-zA-Z0-9+&@#/%?=~_|!:,.;]*[-a-zA-Z0-9+&@#/%=~_|]/g,
            error_text: 'Please enter a valid address.',
            type: InputType.URL
        }
    ]

    public constructor(private form: HTMLFormElement) {
        super()
        this.initSubmitButtons()
        this.initValidation()
    }

    /**
     * Get all inputs from the containers array then sets their listeners
     * @param inputContainers
     */
    private initValidation(): void {
        this.inputContainers = this.getInputContainers()
        for (const container of this.inputContainers) {
            this.getElements<
                | HTMLInputElement
                | HTMLSelectElement
                | HTMLTextAreaElement
                | HTMLFieldSetElement
            >(
                `${HTMLElementType.INPUT}, ${HTMLElementType.SELECT}, ${HTMLElementType.TEXTAREA}, .${CLASS_NAME.common.formCheckboxGroup}`,
                container
            ).map((input) => {
                if (input instanceof HTMLFieldSetElement) {
                    this.setCheckboxGroupListeners(input)
                    return
                }
                if (input.name === this.RECAPTCHA) {
                    return
                }
                if (input instanceof HTMLInputElement) {
                    this.changeInputType(input)
                }
                this.setInputListeners(input)
            })
        }
    }

    /**
     * Find all input containers within the form
     * @returns HTMLElement[input containers]
     */
    private getInputContainers(): HTMLElement[] {
        return this.getElements(`.${CLASS_NAME.common.formField}`, this.form)
    }

    /**
     * Find all submit buttons within the form and set their event listeners
     */
    private initSubmitButtons(): void {
        this.submitButtons = this.getSubmitButtons()
        for (const button of this.submitButtons) {
            this.setSubmitListeners(button)
        }
    }

    /**
     * Find all submit buttons within the form
     * @returns HTMLElement[submit buttons]
     */
    private getSubmitButtons(): HTMLElement[] {
        return this.getElements(
            `[${HtmlAttr.TYPE}=${InputType.SUBMIT}]`,
            this.form
        )
    }

    /**
     * Sets event listeners and aligns proper validations
     * @param fieldset
     */
    private setCheckboxGroupListeners(fieldset: HTMLFieldSetElement): void {
        const checkboxes = this.getElements<HTMLInputElement>(
            `[${HtmlAttr.TYPE}=${InputType.CHECKBOX}]`,
            fieldset
        )
        for (const checkbox of checkboxes) {
            checkbox.addEventListener(EventType.CHANGE, () => {
                this.validateCheckboxGroup(fieldset)
            })
        }
    }

    /**
     * Sets event listeners and aligns proper validations
     * @param input
     */
    private setInputListeners(
        input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    ): void {
        input.addEventListener(EventType.BLUR, () => {
            this.validateInput(input)
        })
        input.addEventListener(EventType.CHANGE, () => {
            this.validateInput(input)
        })
        input.addEventListener(EventType.KEYUP, () => {
            if (input.type === InputType.TEL) {
                input.value = this.formatPhoneNumber(input.value)
            }
            if (input.hasAttribute(ATTR_NAME.main.formValidator.error)) {
                this.validateInput(input)
            }
        })
    }

    /**
     * Checks for all errors to be cleared before allowing the form to post to server
     * @param submit
     */
    private setSubmitListeners(submit: HTMLElement): void {
        submit.addEventListener(EventType.CLICK, (event) => {
            this.validateAllInputs()
            const errorMessages = this.getElements<HTMLElement>(
                `[${ATTR_NAME.main.formValidator.error}]`,
                this.form
            )
            if (errorMessages.length) {
                event.preventDefault()
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const firstError = errorMessages[0].parentElement!
                firstError.scrollIntoView()

                const firstErrorInputs = this.getElements<
                    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                >(
                    `${HTMLElementType.INPUT}, ${HTMLElementType.SELECT}, ${HTMLElementType.TEXTAREA}`,
                    firstError
                )
                firstErrorInputs[0].focus()
            }
        })
    }

    /**
     * Check each input has the correct type and correct if not
     * @param input
     */
    private changeInputType(input: HTMLInputElement): void {
        for (const type of this.validationTypes) {
            if (
                type.type &&
                type.names &&
                type.names.includes(input.name) &&
                type.type !== input.type
            ) {
                input.type = type.type
                return
            }
        }
    }

    /**
     * Scans through all input containers and finds all input fields and initiates validate
     */
    private validateAllInputs(): void {
        for (const container of this.inputContainers) {
            this.getElements<
                | HTMLInputElement
                | HTMLSelectElement
                | HTMLTextAreaElement
                | HTMLFieldSetElement
            >(
                `${HTMLElementType.INPUT}, ${HTMLElementType.SELECT}, ${HTMLElementType.TEXTAREA}, .${CLASS_NAME.common.formCheckboxGroup}`,
                container
            ).map((input) => {
                if (input instanceof HTMLFieldSetElement) {
                    this.validateCheckboxGroup(input)
                    return
                }
                if (input.name === this.RECAPTCHA) {
                    return
                }
                this.validateInput(input)
            })
        }
    }

    /**
     * Validates fieldset of checkboxes has at least 1 selected
     * @param fieldset
     */
    private validateCheckboxGroup(fieldset: HTMLFieldSetElement): void {
        const checkboxes = this.getElements<HTMLInputElement>(
            `[${HtmlAttr.TYPE}=${InputType.CHECKBOX}]`,
            fieldset
        )
        if (
            checkboxes.filter((checkbox) => checkbox.checked === true).length >
            0
        ) {
            this.clearError(fieldset)
        } else {
            this.setError(
                fieldset,
                HtmlAttr.REQUIRED,
                this.CHECKBOX_GROUP_ERROR_TEXT
            )
        }
    }

    /**
     * Validates each input field
     * @param input
     */
    private validateInput(
        input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    ): void {
        if (input.hasAttribute(HtmlAttr.REQUIRED)) {
            this.checkEmpty(input)
        }
        if (
            !input.hasAttribute(
                `[${ATTR_NAME.main.formValidator.error}="required"]`
            )
        ) {
            if (input.type === InputType.CHECKBOX) {
                return
            }
            this.checkRegex(input)
        }
    }

    /**
     * Checks in input field is empty and sets or clears error state
     * @param input
     */
    private checkEmpty(
        input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    ): void {
        if (
            input instanceof HTMLInputElement &&
            input.type === InputType.CHECKBOX &&
            !input.checked
        ) {
            this.setError(input, HtmlAttr.REQUIRED, this.REQUIRED_ERROR_TEXT)
        } else if (!input.value) {
            this.setError(input, HtmlAttr.REQUIRED, this.REQUIRED_ERROR_TEXT)
        } else {
            this.clearError(input)
        }
    }

    /**
     * Align the correct regex type with field by name
     * @param input
     */
    private checkRegex(
        input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    ): void {
        if (!input.value) {
            return
        }
        for (const type of this.validationTypes) {
            if (type.names && type.names.includes(input.name)) {
                this.regexCheckInit(input, type.regex, type.error_text)
                return
            }
        }
        const defaultType = this.defaultValidation
        this.regexCheckInit(input, defaultType.regex, defaultType.error_text)
    }

    /**
     * Validate the value of input field to its corresponding regex
     * @param input
     * @param type
     */
    private regexCheckInit(
        input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
        regexStr: RegExp,
        errorTxt: string
    ): void {
        const regex = new RegExp(regexStr)
        const regexResult = regex.test(input.value)
        if (regexResult) {
            this.clearError(input)
        } else {
            this.setError(input, 'regex', errorTxt)
        }
    }

    /**
     * Sets the error states & classes for an input field and it's container
     * @param input
     * @param errorType - required | regex
     * @param errorText - error message
     */
    private setError(
        input:
            | HTMLInputElement
            | HTMLSelectElement
            | HTMLTextAreaElement
            | HTMLFieldSetElement,
        errorType: string,
        errorText: string
    ): void {
        this.clearError(input)

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let container = input.parentElement!
        if (!container.classList.contains(CLASS_NAME.common.formField)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            container = container.parentElement!
        }
        container.classList.add(CLASS_NAME.common.formFieldError)
        const fieldName =
            input instanceof HTMLFieldSetElement
                ? `${input.getAttribute(
                      ATTR_NAME.main.formValidator.name
                  )}_error`
                : `${input.name}_error`

        input.setAttribute(AriaAttr.DESCRIBED_BY, fieldName)
        input.setAttribute(ATTR_NAME.main.formValidator.error, errorType)

        const errorMessages = this.getElements<HTMLElement>(
            `.${CLASS_NAME.common.formFieldMessageError}`,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            input.parentElement!
        )

        if (!errorMessages.length) {
            container.insertAdjacentHTML(
                'beforeend',
                `<div class="${CLASS_NAME.common.formFieldMessageError}" id="${fieldName}">${errorText}</div>`
            )
        }
    }

    /**
     * Clears error states and classes from input field and its container
     * @param input
     */
    private clearError(
        input:
            | HTMLInputElement
            | HTMLSelectElement
            | HTMLTextAreaElement
            | HTMLFieldSetElement
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let container = input.parentElement!
        if (!container.classList.contains(CLASS_NAME.common.formField)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            container = container.parentElement!
        }
        container.classList.remove(CLASS_NAME.common.formFieldError)

        input.removeAttribute(ATTR_NAME.main.formValidator.error)
        input.removeAttribute(AriaAttr.DESCRIBED_BY)

        const errorMessages = this.getElements<HTMLElement>(
            `.${CLASS_NAME.common.formFieldMessageError}`,
            container
        )
        for (const errorMessage of errorMessages) {
            errorMessage.remove()
        }
    }

    /**
     * Format phone number in either north american or interational format
     * @param phoneNumber
     * @returns - +1 000-000-0000 or 000-000-0000 formats
     */
    public formatPhoneNumber(phoneNumber: string): string {
        const cleanedNumber = ('' + phoneNumber).replace(/\D/g, '')
        const matchNumber = cleanedNumber.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
        if (matchNumber) {
            const intlCode = matchNumber[1] ? '+1 ' : ''
            // eslint-disable-next-line no-magic-numbers
            return `${intlCode}${matchNumber[2]}-${matchNumber[3]}-${matchNumber[4]}`
        }
        return cleanedNumber
    }
}
